import { Country } from '../interfaces/country.interface';

// Phone numbers are stored as a single international string (parsed by Phonelib
// server-side), so the dial code must be part of the saved number. These pure
// helpers compose that string from a dial code + the national part the vendor
// types, and split a stored number back into (code, national) for editing.

const DEFAULT_DIAL_CODE = '60'; // Malaysia

// The country `phonecode` arrives from the API as a number (e.g. 62), so coerce
// to a string before calling String methods — `(62 || '').replace` would throw
// because a non-zero number is truthy and has no `.replace`.
function digitsOnly(value: string | number): string {
  return String(value ?? '').replace(/[^\d]/g, '');
}

// Reduce whatever the vendor typed to just the national subscriber digits:
// drop a leading '+', a leading copy of the dial code, and national trunk zeros.
export function toNational(input: string, dialCode: string): string {
  let n = (input || '').trim().replace(/[\s\-().]/g, '');
  if (n.startsWith('+')) {
    n = n.slice(1);
  }
  const code = digitsOnly(dialCode);
  if (code && n.startsWith(code)) {
    n = n.slice(code.length);
  }
  n = digitsOnly(n).replace(/^0+/, '');
  return n;
}

// Compose the stored international number, e.g. ('62', '012-345 6789') -> '+62123456789'.
// Returns '' when there is no national part so an empty field stays empty.
export function composeInternational(dialCode: string, input: string): string {
  const code = digitsOnly(dialCode) || DEFAULT_DIAL_CODE;
  const national = toNational(input, code);
  return national ? `+${code}${national}` : '';
}

// Split a stored number into { code, national } for display when editing.
// Matches the longest dial code across all known countries so a number whose
// code differs from the vendor's saved country still splits correctly; falls
// back to the saved country's code (or Malaysia) when nothing matches.
export function splitPhone(
  number: string,
  countries: Country[],
  fallbackCode?: string
): { code: string; national: string } {
  const raw = digitsOnly(number);
  if (!raw) {
    return { code: digitsOnly(fallbackCode || '') || DEFAULT_DIAL_CODE, national: '' };
  }

  let best = '';
  (countries || []).forEach(c => {
    const code = digitsOnly(c.phoneCode || '');
    if (code && raw.startsWith(code) && code.length > best.length) {
      best = code;
    }
  });

  if (!best) {
    const fb = digitsOnly(fallbackCode || '');
    if (fb && raw.startsWith(fb)) {
      best = fb;
    }
  }

  if (best) {
    return { code: best, national: raw.slice(best.length).replace(/^0+/, '') };
  }
  return { code: digitsOnly(fallbackCode || '') || DEFAULT_DIAL_CODE, national: raw.replace(/^0+/, '') };
}
